import settings from '@/data/constants';
import { getUser, isDoctor } from '@/data/service/authService';
import type { Consultation } from '@/hooks/patients/usePatientConsultations';
import { TelecomWaitingRoomEntryStatus } from '@montugroup/pms-api-contracts';
import { DateTime } from 'luxon';
import { useMemo } from 'react';

export const VALID_NEXT_CONSULT_STASUSES = [
  settings.consultationStatus.pending,
  settings.consultationStatus.awaitingDocument,
  settings.consultationStatus.scheduled
];

export interface UseNextConsultationProps {
  consultations: Consultation[];
}

export const useNextConsultation = ({ consultations }: UseNextConsultationProps): Consultation | null => {
  const user = getUser();

  const nextConsult = useMemo(() => {
    const pendingConsults = consultations.filter(
      (consult) => consult.status_id && VALID_NEXT_CONSULT_STASUSES.includes(consult.status_id)
    );

    // find a consult in which a patient is waiting

    const [consultWithWaitingPatient] = pendingConsults.filter(
      (consult) => consult.waiting_room_status === TelecomWaitingRoomEntryStatus.Waiting
    );

    if (consultWithWaitingPatient) {
      return consultWithWaitingPatient;
    }

    // otherwise, find the next pending consult that is not in the waiting room for today

    const today = DateTime.now().startOf('day');

    const consultsToday = pendingConsults.filter((consult) => {
      if (!consult.start_time) {
        return false;
      }

      const diff = DateTime.fromISO(consult.start_time).diff(today, 'days');

      return diff.days > 0 && diff.days < 1;
    });

    const [nextConsult] = isDoctor()
      ? consultsToday.filter((consult) => consult.Doctor?.DoctorUser?.id === user?.id)
      : consultsToday;

    return nextConsult || null;
  }, [consultations, user?.id]);

  return nextConsult;
};
